// src/api/seguridad.ts
// Llamadas tipadas al módulo de seguridad del backend.

import { api, midParam } from './client';

export type EstadoAlerta =
  | 'pendiente'
  | 'en_atencion'
  | 'atendida'
  | 'falsa'
  | 'cancelada'
  | 'sin_respuesta';

export type Alerta = {
  id: number;
  municipio_id: number;
  ciudadano_id: number | null;
  origen: string;
  tipo: 'policial' | 'contactos' | string;
  latitud: string | null;
  longitud: string | null;
  precision_metros: number | null;
  direccion_aprox: string | null;
  telefono_anonimo: string | null;
  zona_id: number | null;
  destacamento_id: number | null;
  turno_id: number | null;
  estado: EstadoAlerta;
  despachada_en: string | null;
  despachada_por: number | null;
  descripcion: string | null;
  atendido_por: number | null;
  atendida_en: string | null;
  cerrada_en: string | null;
  nota_cierre: string | null;
  creado_en: string;
  ciudadano_nombre: string | null;
  destacamento_nombre: string | null;
  zona_nombre: string | null;
  atendido_por_nombre: string | null;
  responsable_activo?: number | null;
};

export type AlertasPage = { items: Alerta[]; total: number; page: number };

/** Alertas del municipio (feed de la central). Página 1 = las más recientes. */
export function alertasList(page = 1): Promise<AlertasPage> {
  return api<AlertasPage>(`/seguridad?action=alertas_list&page=${page}${midParam()}`);
}

/** Despachar (la central valida y avisa al personal de guardia — push #1). */
export function alertaDespachar(id: number): Promise<null> {
  return api<null>('/seguridad?action=alerta_despachar', {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
}

/** Tomar una alerta (pasa a en_atencion, queda a mi nombre). */
export function alertaTomar(id: number, lat?: number, lng?: number): Promise<null> {
  return api<null>('/seguridad?action=alerta_tomar', {
    method: 'POST',
    body: JSON.stringify({ id, latitud_agente: lat, longitud_agente: lng }),
  });
}

/** Cerrar una alerta: 'atendida' (real, resuelta) o 'falsa' (falsa alarma). */
export function alertaCerrar(
  id: number,
  estado: 'atendida' | 'falsa',
  nota_cierre?: string
): Promise<null> {
  return api<null>('/seguridad?action=alerta_cerrar', {
    method: 'POST',
    body: JSON.stringify({ id, estado, nota_cierre }),
  });
}

// ═══ PERSONAL / ROSTER + acceso a la app (Fase 3) ══════════════════════
export type Personal = {
  id: number;
  destacamento_id: number;
  nombre: string;
  apellido: string;
  dni: string | null;
  cargo_id: number | null;
  cargo_nombre: string | null;
  telefono: string | null;
  activo: number;
  admin_usuario_id: number | null;
  cuenta_email: string | null;
  cuenta_rol: string | null;
  cuenta_activa: number | null;
  tiene_push: number;
};

export type Destacamento = {
  id: number;
  nombre: string;
  municipio_id: number;
  activo: number;
};

export type Cargo = { id: number; nombre: string; activo: number };

/** Cargos/rangos globales (Comisario, Oficial, Cabo…) para el selector. */
export function cargosList(): Promise<Cargo[]> {
  return api<Cargo[]>('/seguridad?action=cargos_list');
}

/** Destacamentos del municipio (para el selector cuando el usuario no tiene uno fijo). */
export function destacamentosList(): Promise<Destacamento[]> {
  return api<Destacamento[]>(`/seguridad?action=destacamentos_list&activos=0${midParam()}`);
}

/** Roster del destacamento + estado de cuenta de cada integrante. */
export function personalList(destacamentoId: number): Promise<Personal[]> {
  return api<Personal[]>(`/seguridad?action=personal_list&destacamento_id=${destacamentoId}&activos=0`);
}

/** Agregar a alguien al roster (sin cuenta todavía). */
export function personalCrear(
  destacamentoId: number,
  d: { nombre: string; apellido: string; dni?: string; telefono?: string; cargo_id?: number }
): Promise<{ id: number }> {
  return api('/seguridad?action=personal_crear', {
    method: 'POST',
    body: JSON.stringify({ destacamento_id: destacamentoId, ...d }),
  });
}

/** Editar los datos de un integrante del roster (nombre, apellido, dni, teléfono). */
export function personalEditar(
  id: number,
  d: { nombre: string; apellido: string; dni?: string; telefono?: string; cargo_id?: number }
): Promise<null> {
  return api('/seguridad?action=personal_editar', {
    method: 'POST',
    body: JSON.stringify({ id, ...d }),
  });
}

/** Darle acceso a la app: crea la cuenta de login y la vincula al roster. */
export function personalDarAcceso(
  personalId: number,
  d: { email: string; password: string; rol: 'agente_seguridad' | 'radio_operador' }
): Promise<{ admin_usuario_id: number }> {
  return api('/seguridad?action=personal_dar_acceso', {
    method: 'POST',
    body: JSON.stringify({ personal_id: personalId, ...d }),
  });
}

/** Editar la cuenta de app vinculada (email y/o rol agente↔radio). */
export function personalEditarCuenta(
  personalId: number,
  d: { email?: string; rol?: string }
): Promise<null> {
  return api('/seguridad?action=personal_editar_cuenta', {
    method: 'POST',
    body: JSON.stringify({ personal_id: personalId, ...d }),
  });
}

export function personalResetPass(personalId: number, password: string): Promise<null> {
  return api('/seguridad?action=personal_reset_pass', {
    method: 'POST',
    body: JSON.stringify({ personal_id: personalId, password }),
  });
}

export function personalQuitarAcceso(personalId: number): Promise<null> {
  return api('/seguridad?action=personal_quitar_acceso', {
    method: 'POST',
    body: JSON.stringify({ personal_id: personalId }),
  });
}

// ═══ TURNOS / GUARDIA (Fase 2) ═════════════════════════════════════════
export type TurnoPersona = {
  tp_id: number;
  personal_id: number;
  admin_usuario_id: number | null;
  rol_turno: 'jefe_guardia' | 'agente' | 'radio_operador';
  habilitado: number;
  ingreso_en: string | null;
  egreso_en: string | null;
  nombre: string;
  email: string | null;
  rol_sistema: string | null;
  cargo_nombre: string | null;
  tiene_push: number;
};

export type TurnoActivo = {
  id: number;
  destacamento_id: number;
  tipo: string;
  estado: string;
  fecha_inicio: string;
  personal_activo: number;
  personal: TurnoPersona[];
} | null;

export type AgenteDisp = {
  id: number;
  nombre: string;
  apellido: string;
  dni: string | null;
  admin_usuario_id: number | null;
  cargo_nombre: string | null;
  tiene_push: number;
};

export function turnoActivo(destacamentoId: number): Promise<TurnoActivo> {
  return api<TurnoActivo>(`/seguridad?action=turno_activo_get&destacamento_id=${destacamentoId}`);
}
export function turnoAbrir(destacamentoId: number, tipo: string): Promise<{ id: number }> {
  return api('/seguridad?action=turno_abrir', { method: 'POST', body: JSON.stringify({ destacamento_id: destacamentoId, tipo }) });
}
export function turnoCerrar(turnoId: number, novedades?: string): Promise<{ alertas_pendientes: boolean; releva: boolean; nuevo_turno_id: number | null; nuevo_turno_tipo: string | null }> {
  return api('/seguridad?action=turno_cerrar', { method: 'POST', body: JSON.stringify({ turno_id: turnoId, novedades_cierre: novedades }) });
}
export function agentesDisponibles(destacamentoId: number): Promise<AgenteDisp[]> {
  return api<AgenteDisp[]>(`/seguridad?action=agentes_disponibles&destacamento_id=${destacamentoId}`);
}
export function turnoAgregarPersonal(turnoId: number, personalId: number, rolTurno: string): Promise<{ id: number }> {
  return api('/seguridad?action=turno_agregar_personal', { method: 'POST', body: JSON.stringify({ turno_id: turnoId, personal_id: personalId, rol_turno: rolTurno }) });
}
export function turnoQuitarPersonal(tpId: number): Promise<null> {
  return api('/seguridad?action=turno_quitar_personal', { method: 'POST', body: JSON.stringify({ tp_id: tpId }) });
}
export function turnoToggleHabilitado(tpId: number): Promise<{ habilitado: number }> {
  return api('/seguridad?action=turno_toggle_habilitado', { method: 'POST', body: JSON.stringify({ tp_id: tpId }) });
}

// ═══ PLANIFICACIÓN DE TURNOS (calendario semanal) ══════════════════════
export type TurnoCalPersona = {
  tp_id: number;
  personal_id: number;
  admin_usuario_id: number;
  habilitado: number;
  ingreso_en: string | null;
  egreso_en: string | null;
  nombre: string;
  dni: string | null;
  cargo_nombre: string | null;
  tiene_push: number;
};

export type TurnoCal = {
  id: number;
  tipo: 'mañana' | 'noche' | string;
  estado: 'planificado' | 'activo' | 'cerrado' | 'cancelado' | string;
  fecha_inicio_planificada: string;
  fecha_fin_planificada: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  novedades_apertura: string | null;
  novedades_cierre: string | null;
  cerrado_por_nombre: string | null;
  personal: TurnoCalPersona[];
};

export function turnosCalendario(
  destacamentoId: number, desde: string, hasta: string
): Promise<{ turnos: TurnoCal[]; turno_hora_inicio: string }> {
  return api(`/seguridad?action=turnos_calendario&destacamento_id=${destacamentoId}&fecha_desde=${desde}&fecha_hasta=${hasta}`);
}

/** Genera la grilla planificada (2 turnos/día) para un rango. */
export function turnosPlanificarLote(destacamentoId: number, desde: string, hasta: string): Promise<any> {
  return api('/seguridad?action=turnos_planificar_lote', {
    method: 'POST',
    body: JSON.stringify({ destacamento_id: destacamentoId, fecha_desde: desde, fecha_hasta: hasta }),
  });
}

/** Crea un slot individual (día + tipo). Idempotente. */
export function turnoPlanificadoCrear(destacamentoId: number, fecha: string, tipo: 'mañana' | 'noche'): Promise<{ id: number; ya_existia: boolean }> {
  return api('/seguridad?action=turno_planificado_crear', {
    method: 'POST',
    body: JSON.stringify({ destacamento_id: destacamentoId, fecha, tipo }),
  });
}

/** Reemplaza el personal de un turno planificado (lista de personal_id). */
export function turnoSetPersonal(turnoId: number, personalIds: number[]): Promise<any> {
  return api('/seguridad?action=turno_planificado_set_personal', {
    method: 'POST',
    body: JSON.stringify({ turno_id: turnoId, personal: personalIds }),
  });
}

/** Anula un turno planificado. */
export function turnoCancelar(turnoId: number): Promise<null> {
  return api('/seguridad?action=turno_cancelar', {
    method: 'POST',
    body: JSON.stringify({ turno_id: turnoId }),
  });
}

// ═══ AUDITORÍA (Fase 4) ════════════════════════════════════════════════
export type AuditAccion = {
  id: number; alerta_id: number; tipo: string; detalle: string | null; creado_en: string;
  latitud_agente: string | null; longitud_agente: string | null;
  quien: string | null; alerta_tipo: string; zona_nombre: string | null;
};
export type AuditPresencia = {
  id: number; rol_turno: string; ingreso_en: string | null; egreso_en: string | null;
  quien: string | null; turno_tipo: string; fecha_inicio: string;
};
export type AuditFiltros = { desde?: string; hasta?: string; adminUsuarioId?: number; limit?: number };

export function auditoriaDestacamento(
  destacamentoId: number,
  f: AuditFiltros = {}
): Promise<{ acciones: AuditAccion[]; presencia: AuditPresencia[] }> {
  const q = new URLSearchParams({ action: 'auditoria_destacamento', destacamento_id: String(destacamentoId) });
  if (f.desde) q.set('desde', f.desde);
  if (f.hasta) q.set('hasta', f.hasta);
  if (f.adminUsuarioId) q.set('admin_usuario_id', String(f.adminUsuarioId));
  if (f.limit) q.set('limit', String(f.limit));
  return api(`/seguridad?${q.toString()}`);
}

// ─── Helpers de presentación ───────────────────────────────────────────
export const ESTADO_META: Record<EstadoAlerta, { label: string; color: string; dot: string }> = {
  pendiente:     { label: 'Pendiente',   color: '#dc2626', dot: '#ef4444' },
  en_atencion:   { label: 'En atención', color: '#d97706', dot: '#f59e0b' },
  atendida:      { label: 'Atendida',    color: '#059669', dot: '#10b981' },
  falsa:         { label: 'Falsa',       color: '#64748b', dot: '#94a3b8' },
  cancelada:     { label: 'Cancelada',   color: '#64748b', dot: '#94a3b8' },
  sin_respuesta: { label: 'Sin resp.',   color: '#64748b', dot: '#94a3b8' },
};

export const TIPO_META: Record<string, { label: string; icon: string }> = {
  policial:  { label: 'Emergencia policial', icon: '🚨' },
  contactos: { label: 'Aviso a contactos',   icon: '📞' },
};

export const ESTADOS_ABIERTOS: EstadoAlerta[] = ['pendiente', 'en_atencion'];
export const ESTADOS_CERRADOS: EstadoAlerta[] = ['atendida', 'falsa', 'cancelada', 'sin_respuesta'];
